import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2, Download, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useAutoMapDelegates } from '@/hooks/useImport';
import { apiClient, extractErrorMessage } from '@/lib/api';
import type { ImportReport } from '@/components/excel/ExcelToolbar';
import * as XLSX from 'xlsx';

export default function DriverImportPage() {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';
  const [parsedRows, setParsedRows] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string>('');
  const [report, setReport] = useState<ImportReport | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which side of the business this file belongs to. A row with no division
  // does not show up inside NEOM, Dumpers or Flatbed, so this is asked for
  // up front rather than left to be fixed afterwards.
  const [projectCode, setProjectCode] = useState('');
  const [autoMapResult, setAutoMapResult] = useState<{ matched: number; unmatched: string[]; backfilled: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const autoMapMutation = useAutoMapDelegates();

  function handleFile(file: File) {
    setFileName(file.name);
    setReport(null);
    setError(null);
    setPendingFile(file);
    // Parsed only to show a row count before committing. The file itself is
    // what gets uploaded - the server reads it, so the headings do not have to
    // be renamed to match anything here.
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        setParsedRows(XLSX.utils.sheet_to_json(sheet, { defval: null }));
      } catch {
        setParsedRows([]);
        setError('That file could not be opened as a spreadsheet.');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  // The real template, with every column the system knows about and the
  // allowed values written into it - not the seven-column stub this page used
  // to hand out, whose headings no real sheet ever matched.
  async function downloadTemplate() {
    setError(null);
    try {
      const res = await apiClient.get('/api/v1/excel/drivers/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'jasrhub-drivers-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  }

  async function handleSubmit() {
    if (!pendingFile) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      const qs = `?partial=1&fill=1${projectCode ? `&project=${encodeURIComponent(projectCode)}` : ''}`;
      const { data } = await apiClient.post<ImportReport>(`/api/v1/excel/drivers/import${qs}`, fd);
      setReport(data);
    } catch (e) {
      // Previously this threw into nothing: the upload failed and the screen
      // just sat there looking like it had worked.
      setError(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleAutoMap() {
    const r = await autoMapMutation.mutateAsync();
    setAutoMapResult(r);
  }

  return (
    <div className="min-h-screen w-full bg-background flex flex-col" dir={isRtl ? 'rtl' : 'ltr'}>
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <Logo size={28} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-base leading-none">Bulk Import Drivers</div>
              <div className="text-[11px] text-muted-foreground">Upload Excel — preview — commit — auto-map to PetroApp delegates</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-6 py-8 space-y-6">
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="font-display text-lg mb-3">Step 1 — Get the template</h2>
          <p className="text-sm text-muted-foreground mb-4">
            You do not have to use the template — your own sheet works, with the headings it
            already has. <b>Driver</b>, <b>Iqama</b>, <b>Iqama Expiry Date</b>, <b>DOB</b>,{' '}
            <b>Nationality</b> and <b>Phone</b> are all recognised, a Saudi mobile written{' '}
            <b>0536164733</b> or <b>536164733</b> is completed for you, and anyone already in the
            system is matched on their iqama and updated rather than added twice.
          </p>
          <Button variant="outline" onClick={downloadTemplate} className="gap-2">
            <Download className="h-4 w-4" /> Download template
          </Button>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="font-display text-lg mb-3">Step 2 — Upload your file</h2>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => e.target.files && handleFile(e.target.files[0])}
          />
          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:bg-muted/30"
          >
            <FileSpreadsheet className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            {fileName ? (
              <div>
                <div className="font-medium">{fileName}</div>
                <div className="text-sm text-muted-foreground mt-1">
                  {parsedRows.length} rows parsed
                </div>
              </div>
            ) : (
              <div>
                <div className="font-medium">Click to upload Excel or CSV</div>
              </div>
            )}
          </div>
        </div>

        {parsedRows.length > 0 && !report && (
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="font-display text-lg mb-3">Step 3 — Confirm</h2>
            <label className="flex flex-wrap items-center gap-2 text-sm mb-4">
              <span className="text-muted-foreground">Which side do these drivers belong to?</span>
              <select
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
                className="rounded border border-border bg-background px-2 py-1 text-sm"
              >
                <option value="">Not set</option>
                <option value="PRJ-NEOM">NEOM</option>
                <option value="PRJ-RIYADH-WATER">Riyadh Water</option>
                <option value="PRJ-DUMPERS">Dumpers</option>
                <option value="PRJ-RIYADH">Flatbed</option>
              </select>
            </label>
            {!projectCode && (
              <p className="text-xs text-amber-600 mb-4">
                Left unset, these drivers are filed under no division and will not appear inside
                NEOM, Dumpers or Flatbed.
              </p>
            )}
            <Button onClick={handleSubmit} disabled={busy} className="gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import {parsedRows.length} driver(s)
            </Button>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">Nothing was imported.</div>
              <div className="mt-1">{error}</div>
            </div>
          </div>
        )}

        {report && (
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="font-display text-lg mb-3 flex items-center gap-2">
              {report.ok ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-500" />
              )}
              {report.ok
                ? `Imported — ${report.toCreate} added, ${report.toUpdate} updated`
                : 'Nothing was imported — the file was refused'}
            </h2>

            {/* Say plainly what happened to each row, so "it did not import"
                can never again be a silent outcome. */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
              <Stat label="Rows read" value={report.totalRows} />
              <Stat label="Added" value={report.toCreate} className="text-emerald-600" />
              <Stat label="Updated" value={report.toUpdate} className="text-sky-600" />
              <Stat label="Already correct" value={report.unchanged} />
              <Stat
                label="Set aside"
                value={report.skipped ?? 0}
                className={(report.skipped ?? 0) > 0 ? 'text-amber-600' : ''}
              />
            </div>

            {(report.matchedExisting ?? 0) > 0 && (
              <p className="text-sm rounded border border-sky-500/40 bg-sky-500/10 p-3 text-sky-700 mb-3">
                <b>{report.matchedExisting} of these were already in the system</b> and were updated
                rather than added again, matched on {report.matchedOn ?? 'their identifier'}.
              </p>
            )}
            {(report.ignoredColumns?.length ?? 0) > 0 && (
              <p className="text-sm rounded border border-border bg-muted/30 p-3 text-muted-foreground mb-3">
                Columns passed over because this system has nowhere to put them:{' '}
                <b className="text-foreground">{report.ignoredColumns!.join(', ')}</b>. Everything
                else in the file was read.
              </p>
            )}
            {(report.filled ?? 0) > 0 && (
              <p className="text-sm rounded border border-amber-500/40 bg-amber-500/10 p-3 text-amber-700 mb-3">
                <b>{report.filled} were created with stand-in values</b> for what the sheet did not
                carry. They are tagged in Notes, and anything given a stand-in date reads as expired
                until you correct it.
              </p>
            )}

            {report.errors.length > 0 && (
              <details className="mt-4" open>
                <summary className="cursor-pointer font-medium text-sm">
                  {report.errors.length} problem(s) — nothing was saved
                </summary>
                <ul className="mt-2 text-sm space-y-1">
                  {report.errors.slice(0, 100).map((e, i) => (
                    <li key={i}><strong>Row {e.row} · {e.column}:</strong> {e.message}</li>
                  ))}
                </ul>
              </details>
            )}
            {(report.skippedRows?.length ?? 0) > 0 && (
              <details className="mt-4">
                <summary className="cursor-pointer font-medium text-sm">
                  {report.skippedRows!.length} row(s) set aside — finish these by hand
                </summary>
                <ul className="mt-2 text-sm space-y-1">
                  {report.skippedRows!.slice(0, 100).map((s, i) => (
                    <li key={i}><strong>Row {s.row}:</strong> {s.reason}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="font-display text-lg mb-3">Step 4 — Auto-map PetroApp delegates to drivers</h2>
          <p className="text-sm text-muted-foreground mb-4">
            After importing drivers, run this to link existing PetroApp transactions
            (e.g. "delegate: Attiq") to driver records by name match. Future transactions
            will auto-link too.
          </p>
          <Button variant="outline" onClick={handleAutoMap} disabled={autoMapMutation.isPending} className="gap-2">
            {autoMapMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Auto-map delegates
          </Button>
          {autoMapResult && (
            <div className="mt-4 text-sm">
              <div><strong>{autoMapResult.matched}</strong> delegate name(s) matched to drivers</div>
              <div><strong>{autoMapResult.backfilled}</strong> existing fuel transaction(s) attributed</div>
              {autoMapResult.unmatched.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer">Unmatched ({autoMapResult.unmatched.length})</summary>
                  <ul className="mt-1 text-muted-foreground">
                    {autoMapResult.unmatched.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase">{label}</div>
      <div className={`text-2xl font-display ${className ?? ''}`}>{value}</div>
    </div>
  );
}
